import { inject, Injectable, resource } from '@angular/core';
import { HttpClient, httpResource, HttpResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { toFormData } from '../../../utils/helper';
import { ToolJob, ToolJobLogs } from '../../../utils/models/tool.models';
import { firstValueFrom } from 'rxjs';
import { CapabilityStore } from '../../capability.store';

/**
 * Service for interacting with tool jobs.
 */
@Injectable({
  providedIn: 'root',
})
export class ToolJobService {
  /** Inject services. */
  private readonly httpService = inject(HttpClient);
  private readonly capabilityStore = inject(CapabilityStore);

  getJobById(toolId: number, jobId: number | undefined) {
    const history = this.capabilityStore.jobHistory(toolId);
    const cachedJob = history.items().find(job => job.toolJobId === jobId);
    let previousStatus = cachedJob?.status;

    return resource<ToolJob | undefined, number | undefined>({
      defaultValue: cachedJob,
      params: () => jobId,
      loader: async ({ params }) => {
        const freshJob = await firstValueFrom(
          this.httpService.get<ToolJob>(`external/tools/jobs/${params}`)
        );
        if (previousStatus && previousStatus !== freshJob.status) history.refresh();
        previousStatus = freshJob.status;
        return freshJob;
      },
    });
  }

  getOutputs(jobId: () => number | undefined) {
    return httpResource<Record<string, unknown>>(() => {
      const id = jobId();
      if (id) return `external/tools/jobs/${id}/outputs`;
      return undefined;
    });
  }

  getJobLogsById(jobId: number): Promise<ToolJobLogs> {
    return firstValueFrom(this.httpService.get<ToolJobLogs>(`internal/tools/jobs/${jobId}/logs`));
  }

  getOutputById(fileId: number): Promise<HttpResponse<Blob>> {
    const res = this.httpService.get(`external/tools/jobs/outputs/${fileId}`, {
      observe: 'response',
      responseType: 'blob',
    });
    return firstValueFrom(res);
  }

  async createJob(toolId: number, toolInputData: FormGroup): Promise<{ jobId: number }> {
    // Change FormGroup to FormData to pass files and form values
    const formData = toFormData(toolInputData);
    formData.append('tool_id', toolId.toString());
    const res = this.httpService.post<{ jobId: number }>('external/tools/jobs', formData);
    const result = await firstValueFrom(res);
    this.capabilityStore.refreshToolJobHistory(toolId);
    return result;
  }

  async deleteJobById(toolId: number, jobId: number): Promise<{ success: boolean; msg: string }> {
    const res = this.httpService.delete<{ success: boolean; msg: string }>(
      `external/tools/jobs/${jobId}`
    );
    const result = await firstValueFrom(res);
    this.capabilityStore.refreshToolJobHistory(toolId);
    return result;
  }
}
